import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/structured-logger';
import { ServiceError } from '@/lib/service-error';
import type {
  OrgSocialAccount,
  CompanyPagePermission,
  CompanyPageAuditLog,
  PermissionLevel,
  OrgSocialAccountWithPermissions,
} from '@/types/company-pages';

const serviceLogger = logger.child({ service: 'CompanyPagesService' });

export const CompanyPagesService = {
  /**
   * Get all company pages connected to an organization
   */
  async getOrgCompanyPages(organizationId: string): Promise<OrgSocialAccount[]> {
    try {
      const { data, error } = await supabase
        .from('organization_social_accounts')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        serviceLogger.error('Failed to fetch company pages', { error, organizationId });
        throw ServiceError.fromSupabaseError(error, 'getOrgCompanyPages', 'organization_social_accounts');
      }

      return data || [];
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to fetch company pages');
    }
  },

  /**
   * Get company pages the current user can post to
   */
  async getUserAuthorizedPages(userId: string, organizationId: string): Promise<OrgSocialAccountWithPermissions[]> {
    try {
      const { data, error } = await supabase
        .from('organization_social_accounts')
        .select(`
          *,
          company_page_permissions!inner(permission_level)
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .eq('company_page_permissions.user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        serviceLogger.error('Failed to fetch authorized pages', { error, userId, organizationId });
        throw ServiceError.fromSupabaseError(error, 'getUserAuthorizedPages', 'organization_social_accounts');
      }

      return data || [];
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to fetch authorized pages');
    }
  },

  /**
   * Get permissions for a specific page
   */
  async getPagePermissions(orgSocialAccountId: string): Promise<CompanyPagePermission[]> {
    try {
      const { data, error } = await supabase
        .from('company_page_permissions')
        .select('*')
        .eq('org_social_account_id', orgSocialAccountId)
        .order('created_at', { ascending: false });

      if (error) {
        serviceLogger.error('Failed to fetch page permissions', { error, orgSocialAccountId });
        throw ServiceError.fromSupabaseError(error, 'getPagePermissions', 'company_page_permissions');
      }

      return data || [];
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to fetch page permissions');
    }
  },

  /**
   * Grant permission to a user (with audit logging)
   */
  async grantPermission(orgSocialAccountId: string, userId: string, level: PermissionLevel): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke('manage-company-page-permissions', {
        body: {
          action: 'grant',
          orgSocialAccountId,
          targetUserId: userId,
          permissionLevel: level,
        },
      });

      if (error) {
        serviceLogger.error('Failed to grant permission', { error, orgSocialAccountId, userId });
        throw ServiceError.internal('Failed to grant permission');
      }

      serviceLogger.info('Permission granted', { orgSocialAccountId, userId, level });
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to grant permission');
    }
  },

  /**
   * Revoke permission from a user (with audit logging)
   */
  async revokePermission(orgSocialAccountId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke('manage-company-page-permissions', {
        body: {
          action: 'revoke',
          orgSocialAccountId,
          targetUserId: userId,
        },
      });

      if (error) {
        serviceLogger.error('Failed to revoke permission', { error, orgSocialAccountId, userId });
        throw ServiceError.internal('Failed to revoke permission');
      }

      serviceLogger.info('Permission revoked', { orgSocialAccountId, userId });
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to revoke permission');
    }
  },

  /**
   * Disconnect a company page
   */
  async disconnectPage(orgSocialAccountId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('organization_social_accounts')
        .update({ is_active: false })
        .eq('id', orgSocialAccountId);

      if (error) {
        serviceLogger.error('Failed to disconnect page', { error, orgSocialAccountId });
        throw ServiceError.fromSupabaseError(error, 'disconnectPage', 'organization_social_accounts');
      }

      // Log disconnection
      const { data: user } = await supabase.auth.getUser();
      const { data: page } = await supabase
        .from('organization_social_accounts')
        .select('organization_id')
        .eq('id', orgSocialAccountId)
        .maybeSingle();

      if (user.user && page) {
        await supabase.from('company_page_permission_audit').insert({
          org_social_account_id: orgSocialAccountId,
          organization_id: page.organization_id,
          action: 'page_disconnected',
          performed_by: user.user.id,
        });
      }

      serviceLogger.info('Page disconnected', { orgSocialAccountId });
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to disconnect page');
    }
  },

  /**
   * Get audit log for organization
   */
  async getPermissionAuditLog(organizationId: string, limit: number = 100): Promise<CompanyPageAuditLog[]> {
    try {
      const { data, error } = await supabase
        .from('company_page_permission_audit')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        serviceLogger.error('Failed to fetch audit log', { error, organizationId });
        throw ServiceError.fromSupabaseError(error, 'getPermissionAuditLog', 'company_page_permission_audit');
      }

      return data || [];
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to fetch audit log');
    }
  },

  /**
   * Initiate OAuth for company page
   */
  async initiateCompanyPageOAuth(platform: 'linkedin' | 'facebook'): Promise<{ authUrl: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('oauth-start-company-page', {
        body: { platform },
      });

      if (error || !data?.authUrl) {
        serviceLogger.error('Failed to initiate OAuth', { error, platform });
        throw ServiceError.internal('Failed to initiate OAuth');
      }

      serviceLogger.info('OAuth initiated', { platform });
      return data;
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to initiate OAuth');
    }
  },

  /**
   * Fetch available company pages after OAuth
   */
  async fetchCompanyPages(platform: 'linkedin' | 'facebook'): Promise<any[]> {
    try {
      const { data, error } = await supabase.functions.invoke('fetch-company-pages', {
        body: { platform },
      });

      if (error || !data?.pages) {
        serviceLogger.error('Failed to fetch pages', { error, platform });
        throw ServiceError.internal('Failed to fetch pages');
      }

      serviceLogger.info('Pages fetched', { platform, count: data.pages.length });
      return data.pages;
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to fetch pages');
    }
  },

  /**
   * Connect a selected company page
   */
  async connectCompanyPage(
    organizationId: string,
    platform: 'linkedin' | 'facebook',
    pageData: {
      page_id: string;
      page_name: string;
      page_url?: string;
      access_token: string;
      refresh_token?: string;
      profile_picture_url?: string;
    }
  ): Promise<OrgSocialAccount> {
    try {
      const { data: user } = await supabase.auth.getUser();

      if (!user.user) {
        throw ServiceError.unauthorized('User not authenticated');
      }

      const { data, error } = await supabase
        .from('organization_social_accounts')
        .insert({
          organization_id: organizationId,
          platform,
          page_id: pageData.page_id,
          page_name: pageData.page_name,
          page_url: pageData.page_url,
          access_token: pageData.access_token,
          refresh_token: pageData.refresh_token,
          profile_picture_url: pageData.profile_picture_url,
          connected_by: user.user.id,
        })
        .select()
        .maybeSingle();

      if (error) {
        serviceLogger.error('Failed to connect page', { error, organizationId, platform });
        throw ServiceError.fromSupabaseError(error, 'connectCompanyPage', 'organization_social_accounts');
      }

      if (!data) {
        throw ServiceError.internal('No data returned from page connection');
      }

      serviceLogger.info('Page connected', { organizationId, platform, pageId: pageData.page_id });
      return data;
    } catch (error) {
      if (ServiceError.isServiceError(error)) throw error;
      throw ServiceError.internal('Failed to connect page');
    }
  },
};
